import { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { authFetch } from '../../lib/auth-fetch';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  CalendarDays, ChevronLeft, ChevronRight, Plus, X, Trash2, Pencil, Users as UsersIcon, MapPin,
} from 'lucide-react';

const CATEGORIES = [
  { id: 'meeting',     label: 'Meeting',      color: '#3b82f6' },
  { id: 'appointment', label: 'Kundentermin', color: '#9ec043' },
  { id: 'task',        label: 'Aufgabe',      color: '#f59e0b' },
  { id: 'leave',       label: 'Abwesenheit',  color: '#a855f7' },
  { id: 'training',    label: 'Schulung',     color: '#0ea5e9' },
  { id: 'other',       label: 'Sonstiges',    color: '#64748b' },
];

const CAT_META = Object.fromEntries(CATEGORIES.map(c => [c.id, c]));

const VIEWS = [
  { id: 'month', label: 'Monat' },
  { id: 'week',  label: 'Woche' },
  { id: 'day',   label: 'Tag' },
];

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

function startOfDay(d) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function endOfDay(d)   { const x = new Date(d); x.setHours(23, 59, 59, 999); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function startOfWeek(d) {
  // Monday-based
  const x = startOfDay(d); const wd = (x.getDay() + 6) % 7; return addDays(x, -wd);
}
function startOfMonth(d) { const x = new Date(d.getFullYear(), d.getMonth(), 1); x.setHours(0, 0, 0, 0); return x; }
function endOfMonth(d)   { const x = new Date(d.getFullYear(), d.getMonth() + 1, 0); x.setHours(23, 59, 59, 999); return x; }
// Lokales YYYY-MM-DD (NICHT UTC). `toISOString().slice(0,10)` liefert in
// Berlin (CEST UTC+2) bei Mitternachts-Dates den Vortag — was den
// „Heute"-Marker und die from/to-URL-Parameter um einen Tag verschiebt.
function fmtISODate(d) {
  const x = d instanceof Date ? d : new Date(d);
  const pad = n => String(n).padStart(2, '0');
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`;
}
function fmtDateTimeLocal(d) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
function parseLocalDateTime(s) {
  // 'YYYY-MM-DDTHH:MM' as LOCAL time
  return new Date(s);
}

export default function TeamCalendarPage() {
  const { user } = useAuth();
  const [view, setView] = useState('month');
  const [cursor, setCursor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterCat, setFilterCat] = useState('');
  const [filterAttendee, setFilterAttendee] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const range = useMemo(() => {
    if (view === 'month') {
      const s = addDays(startOfWeek(startOfMonth(cursor)), 0);
      const e = addDays(s, 42); // 6 weeks grid
      return { start: s, end: e };
    }
    if (view === 'week') {
      const s = startOfWeek(cursor);
      return { start: s, end: addDays(s, 7) };
    }
    return { start: startOfDay(cursor), end: endOfDay(cursor) };
  }, [view, cursor]);

  const loadEvents = useCallback(async () => {
    const params = new URLSearchParams({ from: fmtISODate(range.start), to: fmtISODate(range.end) });
    const r = await authFetch(`/api/calendar/events?${params}`);
    if (r.ok) { const d = await r.json(); setEvents(d.events || []); }
  }, [range]);

  useEffect(() => { loadEvents(); }, [loadEvents]);
  useEffect(() => {
    (async () => {
      // Teilnehmer-Liste für ALLE internen EES-Rollen laden — der
      // /api/auth/users-Endpoint filtert B2B-Shop-Kunden ohnehin raus.
      // Nur „customer" (Shop-Kunde) bekommt die Liste nicht.
      if (!user?.role || user.role === 'customer') return;
      const r = await authFetch('/api/auth/users');
      if (r.ok) setUsers(await r.json());
    })();
  }, [user]);

  const filteredEvents = useMemo(() => events.filter(e => {
    if (filterCat && e.category !== filterCat) return false;
    if (filterAttendee && !(e.attendees || []).includes(filterAttendee) && e.createdBy !== filterAttendee) return false;
    return true;
  }), [events, filterCat, filterAttendee]);

  const openNew = (presetStart) => {
    const start = presetStart || new Date();
    start.setMinutes(0, 0, 0);
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    setEditing({
      title: '', description: '',
      start: fmtDateTimeLocal(start), end: fmtDateTimeLocal(end),
      allDay: false, attendees: [], category: 'meeting', location: '',
      reminders: [60],  // Default: 1h vorher
    });
    setShowForm(true);
  };

  const openEdit = (ev) => {
    setEditing({
      id: ev.id,
      title: ev.title, description: ev.description || '',
      start: fmtDateTimeLocal(new Date(ev.start)), end: fmtDateTimeLocal(new Date(ev.end)),
      allDay: !!ev.allDay, attendees: ev.attendees || [],
      category: ev.category || 'meeting', location: ev.location || '',
      reminders: ev.reminders || [],
      createdBy: ev.createdBy,
    });
    setShowForm(true);
  };

  const canEdit = (ev) => user?.role === 'admin' || ev.createdBy === user?.id;

  const saveEvent = async () => {
    if (!editing.title?.trim()) { alert('Titel erforderlich'); return; }
    const body = {
      title: editing.title.trim(),
      description: editing.description || '',
      start: parseLocalDateTime(editing.start).toISOString(),
      end: parseLocalDateTime(editing.end).toISOString(),
      allDay: !!editing.allDay,
      attendees: editing.attendees || [],
      category: editing.category,
      location: editing.location || '',
      reminders: (editing.reminders || []).filter(r => Number.isFinite(r) && r > 0),
    };
    const isEdit = !!editing.id;
    const r = await authFetch(
      isEdit ? `/api/calendar/events/${editing.id}` : '/api/calendar/events',
      {
        method: isEdit ? 'PATCH' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      },
    );
    if (r.ok) { setShowForm(false); setEditing(null); loadEvents(); }
    else { const d = await r.json().catch(() => ({})); alert(d.detail || 'Fehler beim Speichern'); }
  };

  const deleteEvent = async () => {
    if (!editing?.id) return;
    if (!window.confirm('Diesen Termin wirklich löschen?')) return;
    const r = await authFetch(`/api/calendar/events/${editing.id}`, { method: 'DELETE' });
    if (r.ok) { setShowForm(false); setEditing(null); loadEvents(); }
    else { alert('Löschen fehlgeschlagen'); }
  };

  const jumpPrev = () => setCursor(c => {
    if (view === 'month') return new Date(c.getFullYear(), c.getMonth() - 1, 1);
    if (view === 'week')  return addDays(c, -7);
    return addDays(c, -1);
  });
  const jumpNext = () => setCursor(c => {
    if (view === 'month') return new Date(c.getFullYear(), c.getMonth() + 1, 1);
    if (view === 'week')  return addDays(c, 7);
    return addDays(c, 1);
  });

  const title = useMemo(() => {
    if (view === 'month') return cursor.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
    if (view === 'week') {
      const s = startOfWeek(cursor); const e = addDays(s, 6);
      return `${s.toLocaleDateString('de-DE', { day: '2-digit', month: 'short' })} – ${e.toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' })}`;
    }
    return cursor.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
  }, [cursor, view]);

  return (
    <div className="p-6 max-w-7xl">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-bold text-[#32373c] flex items-center gap-2" data-testid="calendar-title">
            <CalendarDays className="w-5 h-5 text-[#9ec043]" /> Team-Kalender
          </h1>
          <p className="text-xs text-gray-400">Interne Terminübersicht — alle Mitarbeitertermine</p>
        </div>
        <Button onClick={() => openNew(new Date())} className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-sm" data-testid="btn-new-event">
          <Plus className="w-4 h-4 mr-1" /> Neuer Termin
        </Button>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl border border-gray-200 p-3 mb-3 flex flex-wrap items-center gap-2">
        <button onClick={jumpPrev} className="p-1.5 rounded hover:bg-gray-100" data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></button>
        <button onClick={() => setCursor(new Date())} className="text-xs px-3 py-1.5 border border-gray-200 rounded hover:bg-gray-50" data-testid="cal-today">Heute</button>
        <button onClick={jumpNext} className="p-1.5 rounded hover:bg-gray-100" data-testid="cal-next"><ChevronRight className="w-4 h-4" /></button>
        <div className="text-sm font-semibold text-[#32373c] ml-2" data-testid="cal-range-title">{title}</div>

        <div className="ml-auto flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
            {VIEWS.map(v => (
              <button key={v.id} onClick={() => setView(v.id)}
                className={`text-xs px-3 py-1 rounded ${view === v.id ? 'bg-white shadow-sm text-[#32373c] font-medium' : 'text-gray-500 hover:text-gray-700'}`}
                data-testid={`cal-view-${v.id}`}>{v.label}</button>
            ))}
          </div>

          <select value={filterCat} onChange={e => setFilterCat(e.target.value)} className="text-xs h-8 border border-gray-200 rounded px-2" data-testid="cal-filter-cat">
            <option value="">Alle Kategorien</option>
            {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>

          {users.length > 0 && (
            <select value={filterAttendee} onChange={e => setFilterAttendee(e.target.value)} className="text-xs h-8 border border-gray-200 rounded px-2" data-testid="cal-filter-attendee">
              <option value="">Alle Mitarbeiter</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          )}
        </div>
      </div>

      {/* Grid */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {view === 'month' && (
          <MonthView cursor={cursor} events={filteredEvents} onPickDate={openNew} onPickEvent={openEdit} />
        )}
        {view === 'week' && (
          <WeekView cursor={cursor} events={filteredEvents} onPickSlot={openNew} onPickEvent={openEdit} />
        )}
        {view === 'day' && (
          <DayView cursor={cursor} events={filteredEvents} onPickSlot={openNew} onPickEvent={openEdit} />
        )}
      </div>

      {/* Form modal */}
      {showForm && editing && (
        <EventForm
          editing={editing} setEditing={setEditing}
          onCancel={() => { setShowForm(false); setEditing(null); }}
          onSave={saveEvent} onDelete={editing.id ? deleteEvent : null}
          users={users}
          canEdit={!editing.id || canEdit({ createdBy: editing.createdBy })}
        />
      )}
    </div>
  );
}

function EventPill({ ev, onClick, compact }) {
  const meta = CAT_META[ev.category] || CAT_META.other;
  // Farb-Priorität: erste Attendee-Farbe > Creator-Farbe > explizite Event-Farbe > Kategorie-Default
  const color = (ev.attendeeColors && ev.attendeeColors[0])
    || ev.creatorColor
    || ev.color
    || meta.color;
  const start = new Date(ev.start);
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick(ev); }}
      className="w-full text-left rounded px-1.5 py-0.5 text-[10px] truncate hover:opacity-90"
      style={{ backgroundColor: color + '22', color, borderLeft: `3px solid ${color}` }}
      data-testid={`event-pill-${ev.id}`}
      title={ev.title + (ev.allDay ? ' (Ganztägig)' : '')}
    >
      {!compact && !ev.allDay && <span className="tabular-nums mr-1">{start.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}</span>}
      {!compact && ev.allDay && <span className="text-[9px] uppercase opacity-70 mr-1">Ganzt.</span>}
      <span className="font-medium">{ev.title}</span>
    </button>
  );
}

function MonthView({ cursor, events, onPickDate, onPickEvent }) {
  const first = startOfMonth(cursor);
  const gridStart = startOfWeek(first);
  const days = Array.from({ length: 42 }, (_, i) => addDays(gridStart, i));
  const byDate = useMemo(() => {
    // Mehrtägige Termine werden in JEDEN Tag zwischen Start und Ende
    // einsortiert (inkl. Start- und Enddatum), damit der Termin im Monats-
    // Raster über alle betroffenen Zellen sichtbar ist.
    const m = {};
    for (const ev of events) {
      const s = startOfDay(new Date(ev.start));
      const e = ev.end ? startOfDay(new Date(ev.end)) : s;
      // Schutz gegen invalid/ungekehrte Daten
      const last = e < s ? s : e;
      let cur = s;
      let guard = 0;
      while (cur <= last && guard < 366) {
        const k = fmtISODate(cur);
        (m[k] ||= []).push(ev);
        cur = addDays(cur, 1);
        guard += 1;
      }
    }
    return m;
  }, [events]);

  return (
    <div>
      <div className="grid grid-cols-7 text-[10px] uppercase tracking-wide text-gray-400 border-b border-gray-100 bg-gray-50">
        {WEEKDAYS.map(d => <div key={d} className="px-2 py-1.5 text-center">{d}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d, i) => {
          const inMonth = d.getMonth() === cursor.getMonth();
          const isToday = fmtISODate(d) === fmtISODate(new Date());
          const dayEvents = byDate[fmtISODate(d)] || [];
          return (
            <div key={i}
              onClick={() => onPickDate(new Date(d))}
              className={`min-h-24 p-1.5 border-r border-b border-gray-100 cursor-pointer hover:bg-gray-50/70 ${inMonth ? '' : 'bg-gray-50/40'}`}
              data-testid={`cal-day-${fmtISODate(d)}`}
            >
              <div className={`text-[10px] mb-1 ${isToday ? 'inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#9ec043] text-white font-semibold' : inMonth ? 'text-gray-600' : 'text-gray-300'}`}>
                {d.getDate()}
              </div>
              <div className="space-y-0.5">
                {dayEvents.slice(0, 3).map(ev => (
                  <EventPill key={ev.id} ev={ev} onClick={onPickEvent} compact />
                ))}
                {dayEvents.length > 3 && (
                  <div className="text-[9px] text-gray-400">+ {dayEvents.length - 3} weitere</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function WeekView({ cursor, events, onPickSlot, onPickEvent }) {
  const start = startOfWeek(cursor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  const hours = Array.from({ length: 14 }, (_, i) => i + 7); // 07–20
  const eventsByDay = useMemo(() => {
    // Mehrtägige Termine werden in JEDEN Tag zwischen Start und Ende
    // einsortiert. Für die Wochenansicht erscheint der Termin auf dem
    // Start-Stunden-Slot des Start-Tages — und zusätzlich als
    // „Ganztags"-Banner oben auf den folgenden Tagen.
    const m = {};
    for (const ev of events) {
      const s = startOfDay(new Date(ev.start));
      const e = ev.end ? startOfDay(new Date(ev.end)) : s;
      const last = e < s ? s : e;
      let cur = s;
      let guard = 0;
      while (cur <= last && guard < 8) {
        const k = fmtISODate(cur);
        (m[k] ||= []).push(ev);
        cur = addDays(cur, 1);
        guard += 1;
      }
    }
    return m;
  }, [events]);

  return (
    <div className="overflow-auto">
      <div className="min-w-[720px]">
        <div className="grid" style={{ gridTemplateColumns: '60px repeat(7, 1fr)' }}>
          <div className="border-b border-r border-gray-100 bg-gray-50" />
          {days.map((d, i) => {
            const isToday = fmtISODate(d) === fmtISODate(new Date());
            return (
              <div key={i} className="px-2 py-1.5 text-center border-b border-r border-gray-100 bg-gray-50">
                <div className="text-[10px] uppercase text-gray-400">{WEEKDAYS[i]}</div>
                <div className={`text-sm ${isToday ? 'text-[#9ec043] font-semibold' : 'text-[#32373c]'}`}>{d.getDate()}</div>
              </div>
            );
          })}
        </div>
        <div className="grid" style={{ gridTemplateColumns: '60px repeat(7, 1fr)' }}>
          {hours.map(h => (
            <Fragment key={`row-${h}`}>
              <div className="text-[10px] text-gray-400 px-2 py-2 border-b border-r border-gray-100 tabular-nums">
                {String(h).padStart(2, '0')}:00
              </div>
              {days.map((d, di) => {
                const slot = new Date(d); slot.setHours(h, 0, 0, 0);
                const key = fmtISODate(d);
                const dayKey = fmtISODate(d);
                const evs = (eventsByDay[key] || []).filter(ev => {
                  const evStartKey = fmtISODate(new Date(ev.start));
                  // Auf dem eigentlichen Start-Tag: an der Start-Stunde
                  // einblenden. An Folge-Tagen eines Mehrtages-Termins:
                  // an der ersten sichtbaren Stunde (7:00) einblenden.
                  if (evStartKey === dayKey) {
                    return new Date(ev.start).getHours() === h;
                  }
                  return h === hours[0];
                });
                return (
                  <div key={`${h}-${di}`}
                    onClick={() => onPickSlot(new Date(slot))}
                    className="min-h-12 border-b border-r border-gray-100 p-1 cursor-pointer hover:bg-gray-50/70"
                    data-testid={`cal-slot-${fmtISODate(d)}-${h}`}
                  >
                    <div className="space-y-0.5">
                      {evs.map(ev => <EventPill key={ev.id} ev={ev} onClick={onPickEvent} />)}
                    </div>
                  </div>
                );
              })}
            </Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

function DayView({ cursor, events, onPickSlot, onPickEvent }) {
  const hours = Array.from({ length: 14 }, (_, i) => i + 7);
  const key = fmtISODate(cursor);
  // Mehrtagestermine sind an JEDEM Tag zwischen Start und Ende sichtbar.
  const evs = events.filter(ev => {
    const s = startOfDay(new Date(ev.start));
    const e = ev.end ? startOfDay(new Date(ev.end)) : s;
    const cur = startOfDay(cursor);
    return cur >= s && cur <= e;
  });

  return (
    <div className="divide-y divide-gray-100">
      {hours.map(h => {
        const slot = new Date(cursor); slot.setHours(h, 0, 0, 0);
        const bucket = evs.filter(ev => {
          const evStartKey = fmtISODate(new Date(ev.start));
          if (evStartKey === key) {
            return new Date(ev.start).getHours() === h;
          }
          return h === hours[0];
        });
        return (
          <div key={h}
            onClick={() => onPickSlot(new Date(slot))}
            className="flex gap-3 px-4 py-2 cursor-pointer hover:bg-gray-50/70"
            data-testid={`cal-day-slot-${h}`}
          >
            <div className="w-14 text-xs text-gray-400 tabular-nums pt-0.5 shrink-0">{String(h).padStart(2, '0')}:00</div>
            <div className="flex-1 space-y-1">
              {bucket.length === 0 ? (
                <div className="text-[11px] text-gray-300">—</div>
              ) : bucket.map(ev => <EventPill key={ev.id} ev={ev} onClick={onPickEvent} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EventForm({ editing, setEditing, onCancel, onSave, onDelete, users, canEdit }) {
  const toggleAttendee = (uid) => {
    setEditing(p => {
      const list = p.attendees || [];
      return { ...p, attendees: list.includes(uid) ? list.filter(x => x !== uid) : [...list, uid] };
    });
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onCancel}>
      <div className="bg-white rounded-xl w-full max-w-lg shadow-xl" onClick={e => e.stopPropagation()} data-testid="event-form">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-[#32373c]">{editing.id ? 'Termin bearbeiten' : 'Neuer Termin'}</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-3 max-h-[70vh] overflow-auto">
          <div>
            <Label className="text-xs text-gray-500">Titel *</Label>
            <Input value={editing.title} onChange={e => setEditing(p => ({ ...p, title: e.target.value }))}
              className="mt-1 text-sm" autoFocus disabled={!canEdit} data-testid="event-title" />
          </div>

          <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={!!editing.allDay}
              disabled={!canEdit}
              onChange={(e) => {
                const allDay = e.target.checked;
                setEditing(p => {
                  if (!allDay) return { ...p, allDay: false };
                  // Beim Aktivieren: Uhrzeit raus, Start auf 00:00 / Ende auf 23:59
                  // des jeweiligen Tages anchored.
                  const s = p.start ? new Date(p.start) : new Date();
                  const e2 = p.end ? new Date(p.end) : s;
                  const sIso = `${s.getFullYear()}-${String(s.getMonth()+1).padStart(2,'0')}-${String(s.getDate()).padStart(2,'0')}T00:00`;
                  const eIso = `${e2.getFullYear()}-${String(e2.getMonth()+1).padStart(2,'0')}-${String(e2.getDate()).padStart(2,'0')}T23:59`;
                  return { ...p, allDay: true, start: sIso, end: eIso };
                });
              }}
              data-testid="event-allday"
            />
            <span><strong>Ganztägig</strong> — verschiebt Start auf 00:00, Ende auf 23:59</span>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-500">Start *</Label>
              <Input
                type={editing.allDay ? "date" : "datetime-local"}
                value={editing.allDay ? (editing.start || '').slice(0, 10) : editing.start}
                onChange={e => {
                  const val = e.target.value;
                  setEditing(p => ({ ...p, start: editing.allDay ? `${val}T00:00` : val }));
                }}
                className="mt-1 text-sm" disabled={!canEdit} data-testid="event-start" />
            </div>
            <div>
              <Label className="text-xs text-gray-500">Ende *</Label>
              <Input
                type={editing.allDay ? "date" : "datetime-local"}
                value={editing.allDay ? (editing.end || '').slice(0, 10) : editing.end}
                onChange={e => {
                  const val = e.target.value;
                  setEditing(p => ({ ...p, end: editing.allDay ? `${val}T23:59` : val }));
                }}
                className="mt-1 text-sm" disabled={!canEdit} data-testid="event-end" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-gray-500">Kategorie</Label>
              <select value={editing.category} onChange={e => setEditing(p => ({ ...p, category: e.target.value }))}
                className="mt-1 w-full h-9 border border-gray-200 rounded px-2 text-sm" disabled={!canEdit} data-testid="event-category">
                {CATEGORIES.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs text-gray-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> Ort</Label>
              <Input value={editing.location || ''} onChange={e => setEditing(p => ({ ...p, location: e.target.value }))}
                className="mt-1 text-sm" placeholder="optional" disabled={!canEdit} data-testid="event-location" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-gray-500">Notiz / Beschreibung</Label>
            <textarea value={editing.description || ''} onChange={e => setEditing(p => ({ ...p, description: e.target.value }))}
              className="mt-1 w-full border border-gray-200 rounded p-2 text-sm min-h-20" disabled={!canEdit} data-testid="event-description" />
          </div>

          {users.length > 0 && (
            <div>
              <Label className="text-xs text-gray-500 flex items-center gap-1"><UsersIcon className="w-3 h-3" /> Teilnehmer</Label>
              <div className="mt-1 flex flex-wrap gap-1.5" data-testid="event-attendees">
                {users.map(u => {
                  const on = (editing.attendees || []).includes(u.id);
                  return (
                    <button key={u.id} type="button" disabled={!canEdit}
                      onClick={() => toggleAttendee(u.id)}
                      className={`text-xs px-2 py-1 rounded-full border ${on ? 'bg-[#9ec043]/10 border-[#9ec043] text-[#9ec043] font-medium' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'}`}
                      data-testid={`event-attendee-${u.id}`}>
                      {u.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Reminder-Konfiguration: bis zu 2 Erinnerungs-Offsets */}
          <div data-testid="event-reminders-section">
            <Label className="text-xs text-gray-500 flex items-center gap-1">
              <span>🔔</span> Erinnerungen (max. 2)
            </Label>
            <div className="mt-1 space-y-1.5">
              {([0, 1]).map(slotIdx => {
                const cur = (editing.reminders || [])[slotIdx];
                return (
                  <div key={slotIdx} className="flex items-center gap-2">
                    <select
                      value={cur ?? ''}
                      onChange={(e) => {
                        const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                        setEditing(p => {
                          const next = [...(p.reminders || [])];
                          while (next.length <= slotIdx) next.push(null);
                          next[slotIdx] = v;
                          return { ...p, reminders: next.filter(x => x != null) };
                        });
                      }}
                      disabled={!canEdit}
                      className="flex-1 h-8 text-xs border border-gray-200 rounded px-2 bg-white"
                      data-testid={`event-reminder-${slotIdx}`}
                    >
                      <option value="">— Keine Erinnerung —</option>
                      <option value="5">5 Minuten vorher</option>
                      <option value="15">15 Minuten vorher</option>
                      <option value="30">30 Minuten vorher</option>
                      <option value="60">1 Stunde vorher</option>
                      <option value="120">2 Stunden vorher</option>
                      <option value="240">4 Stunden vorher</option>
                      <option value="1440">1 Tag vorher</option>
                      <option value="2880">2 Tage vorher</option>
                      <option value="10080">1 Woche vorher</option>
                    </select>
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-gray-400 mt-1">
              Versandt per Mail an alle Teilnehmer + den Ersteller. Standard: 1 Stunde vorher.
            </p>
          </div>
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50">
          <div>
            {onDelete && canEdit && (
              <button onClick={onDelete} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1" data-testid="event-delete">
                <Trash2 className="w-3.5 h-3.5" /> Löschen
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onCancel} className="text-sm">Abbrechen</Button>
            {canEdit && (
              <Button onClick={onSave} className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-sm" data-testid="event-save">
                <Pencil className="w-3.5 h-3.5 mr-1" /> Speichern
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
